import { startOfMonth } from "date-fns";
import { z } from "zod";

import { calendarModalsSearchSchema } from "src/contexts/CalendarModalsContext";
import { type CalendarWeekSize } from "src/hooks/useCalendarWeeks";
import { getPreferences } from "src/lib/preferencesCookie";

import {
  getCalendarInitialWeekIndex,
  getCalendarWeekIndex,
  getCalendarWeekStart,
} from "../calendar/calendarWeeks";
import {
  dateSearchParamSchema,
  dateToDateUrl,
  getDateSearchParam,
} from "../calendar/utils";

const calendarViewSchema = z.enum(["month", "week"]);

export type CalendarView = z.infer<typeof calendarViewSchema>;

export const calendarSearchSchema = calendarModalsSearchSchema.extend({
  date: dateSearchParamSchema,
  view: calendarViewSchema.optional().catch(undefined),
});

export interface CalendarInitialView {
  view: CalendarView;
  date: string | undefined;
  initialWeekIndex: number;
}

function getCalendarView(value: string | undefined): CalendarView {
  if (value === "week") {
    return "week";
  }

  return "month";
}

export function getCalendarInitialView(
  href: string,
  monthWeekSize: CalendarWeekSize["type"],
): CalendarInitialView {
  const viewParam = new URL(href, "http://localhost").searchParams.get("view");
  const view = viewParam
    ? getCalendarView(viewParam)
    : getPreferences().calendarView;
  const date = getDateSearchParam(href);

  return {
    view,
    date,
    initialWeekIndex: getCalendarStartWeekIndex(
      getCalendarDefaultWeekIndex(view, monthWeekSize),
      date,
    ),
  };
}

export function getCalendarStartWeekIndex(
  defaultWeekIndex: number,
  date: string | undefined,
): number {
  return date === undefined
    ? defaultWeekIndex
    : getCalendarInitialWeekIndex(date);
}

const CALENDAR_TODAY_MAX_ROW = {
  fixed: 3,
  measured: 2,
} satisfies Record<CalendarWeekSize["type"], number>;

export function getCalendarTodayWeekIndex(): number {
  return getCalendarWeekIndex(dateToDateUrl(new Date()));
}

export function getCalendarDefaultWeekIndex(
  view: CalendarView,
  monthWeekSize: CalendarWeekSize["type"],
): number {
  const todayWeekIndex = getCalendarTodayWeekIndex();

  if (view === "week") {
    return todayWeekIndex;
  }

  return Math.max(
    getCalendarWeekIndex(dateToDateUrl(startOfMonth(new Date()))),
    todayWeekIndex - (CALENDAR_TODAY_MAX_ROW[monthWeekSize] - 1),
  );
}

export function getCalendarDateSearchParam(
  defaultWeekIndex: number,
  weekIndex: number,
): string | undefined {
  return weekIndex === defaultWeekIndex
    ? undefined
    : getCalendarWeekStart(weekIndex);
}
